import { useCallback } from 'react'
import { useDispatch, useSelector } from 'react-redux'
import { useLocation } from 'react-router-dom'
import sanitize from 'sanitize-html'

import { EventBus } from 'core-components/classes/events/Bus'
import { IForm } from 'core-components/classes/models/IForm'
import { IUser } from 'core-components/sitebuilder/models/IUser'
import { selectUser } from 'core-components/sitebuilder/store/selectors/selectUser'
import { setUser } from 'core-components/sitebuilder/store/siteBuilderSlice'
import { saveToCache } from 'src/core/classes/cache/Cache'
import StorageKeys from 'src/core/classes/cache/StorageKeys'
import { calculateRedirectData } from 'src/core/classes/helpers/authorization'
import { getAppOwnerId } from 'src/core/classes/helpers/getAppId'
import { clearStorageBeforeLogin, getClientSecret } from 'src/core/classes/helpers/StorageHelper'
import { proxyIdentity } from 'src/core/classes/helpers/UrlHelper'
import { HttpRequest, Rest } from 'src/core/classes/http'
import { userApi } from 'src/core/classes/services/api/UserApi'
import { prepareHpidLogin, toHpidTarget } from 'src/core/classes/services/auth/hpidV3'
import { authService } from 'src/core/classes/services/AuthService'
import { ADMIN, GUEST } from 'src/core/constants/roles'
import { setAuthError, setUser as setCoreUser } from 'src/core/store/appSlice'
import { pageApi } from 'src/core/store/service/sitebuilder/pageApi'
import {TAuthService} from "../../classes/models/IAuth";

export const userEventBus = new EventBus(['save', 'detach'], 'userEventBus')

export const useUserStore = () => {
  const user = useSelector(selectUser)
  const dispatch = useDispatch<any>()
  const { hash, state, pathname } = useLocation()

  // TODO: check
  const updateCurrentUser = useCallback(
    async (newUser: IUser) => {
      await save(newUser)
      await dispatch(pageApi.endpoints.fetchSitePages.initiate(true))
      await saveToCache(StorageKeys.user, newUser)

      dispatch(setCoreUser(newUser))
      dispatch(setUser(newUser))
    },
    [dispatch]
  )

  // TODO: MOVE TO RTK
  const getTokenApi = useCallback(async (): Promise<{
    AccessToken: string
    RefreshToken: string
  }> => {
    const url = proxyIdentity('/mbe/api/v2/access/token')
    const [clientId, secretId] = getClientSecret(getAppOwnerId())
    if (!clientId || !secretId) {
      dispatch(
        setAuthError({
          result: true,
          message: 'ClientId or SecretId is empty',
          code: 'hp-login',
        })
      )
      window.location.replace(sanitize(`/#redirectPage=${location.pathname}${location.search}`))
      throw new Error('ClientId or SecretId is empty')
    }

    const payload = {
      ClientId: clientId,
      ClientSecret: secretId,
    }

    return await Rest.post({ url, payload })
  }, [dispatch])

  const save = useCallback(async (newUser: IUser, appId?: string) => {
    return await userApi.saveUser(newUser, appId)
  }, [])

  const login = useCallback(
    async (email: string, password: string): Promise<boolean> => {
      const { redirectUrl } = calculateRedirectData({ state, hash, pathname })
      return authService.authorizeByPassport({ email, password, redirectUrl })
    },
    [hash, pathname, state]
  )

  // Read here and returned, not read in useProcessLogin: a module that touches import.meta does
  // not load under ts-jest (CommonJS), and tests mock this hook
  const identityType = import.meta.env.VITE_IDENTITY_TYPE as TAuthService | undefined

  // Returns the URL to open. For HP ID v3 that is the API's start URL, and the caller must
  // navigate to it. sb-hpscoutso (useUserAuthorize.ts) calls this and does location.replace.
  const hpLogin = useCallback(
    async (currentPagePath?: string, additionalPayload: IForm = {}): Promise<string | null> => {
      clearStorageBeforeLogin()
      HttpRequest.SetBlockerCaller(Promise.resolve())

      if (identityType) {
        return await authService.fetchIdentityAuthorizeUrl(identityType, currentPagePath)
      }
      return prepareHpidLogin(currentPagePath ?? '/', toHpidTarget(additionalPayload.Target))
    },
    [identityType]
  )

  const isAdminUser = useCallback(
    (checkUser?: IUser) => {
      const currentUser = checkUser || user

      return !!currentUser.roleIds.find((role) => {
        return role === ADMIN
      })
    },
    [user]
  )

  const isGuestUser = useCallback(
    (checkUser?: IUser) => {
      const currentUser = checkUser || user
      return currentUser.roleIds.length === 1 && currentUser.roleIds[0] === GUEST
    },
    [user]
  )

  return {
    updateCurrentUser,
    login,
    hpLogin,
    identityType,
    isAdminUser,
    save,
    isGuestUser,
    getTokenApi,
  }
}

// TODO: Used in lut. bootstrap
export const updateCurrentUserGlobal = async (newUser: IUser) => {
  await userApi.saveUser(newUser)
  await authService.refreshToken()
  await saveToCache(StorageKeys.user, newUser)
}
